import { Capacitor } from '@capacitor/core';

import type { AuthorizedCpaUser } from '@liscio/api';

/** Mirrors `CpaUser::EMPLOYEE_ROLES` / `#employee?` — keep in sync with olympusapi. */
const FIRM_APP_EMPLOYEE_ROLES = new Set([
  'firm_employee',
  'firm_admin',
  'firm_frontdesk',
  'specialist',
]);

function parseFirmHostnameFromCompanyUrl(
  companyUrl: string | undefined | null
): string | null {
  if (!companyUrl) return null;
  try {
    const raw = companyUrl.startsWith('http')
      ? companyUrl
      : `https://${companyUrl}`;
    return new URL(raw).hostname;
  } catch {
    return null;
  }
}

/**
 * True when the SPA hostname is not the firm CPA's subdomain (from company_url).
 * Used for UX when a client user cannot remain on this host. Does not use
 * config.webApp.url or VITE_WEB_APP_URL.
 */
export function isHostnameOffFirmSubdomain(
  companyUrl: string | undefined | null
): boolean {
  if (Capacitor.isNativePlatform() || typeof window === 'undefined') {
    return false;
  }
  const firmHost = parseFirmHostnameFromCompanyUrl(companyUrl);
  if (!firmHost) return false;
  return window.location.hostname !== firmHost;
}

/**
 * Returns the hand-off target if the just-authenticated user is a client
 * who needs to be redirected to the client app / firm subdomain flow, else null.
 * Does not depend on env-coupled config — derives from the login response.
 */
export function shouldRedirectToFirmSubdomain(
  loggedUser: AuthorizedCpaUser
): { cpaSiteUrl: string } | null {
  if (Capacitor.isNativePlatform()) return null;

  const data = loggedUser.cpa_data;
  if (!data?.company_url) return null;

  const role = data.cpa_user_role;
  if (!role || FIRM_APP_EMPLOYEE_ROLES.has(role)) return null;

  const firmHost = parseFirmHostnameFromCompanyUrl(data.company_url);
  if (!firmHost) return null;

  if (typeof window !== 'undefined' && window.location.hostname === firmHost) {
    return null;
  }

  return { cpaSiteUrl: data.company_url };
}
